import { RegularExpression } from '../core/regular_expression';
import { CurriedFunction1, CurriedFunction2, CurriedFunction3, CurriedFunction4 } from 'lodash4';
import * as _4 from 'lodash4';

type time = number | string | moment.Moment;
type timeunits =
  | 'year'
  | 'years'
  | 'y'
  | 'month'
  | 'months'
  | 'M'
  | 'week'
  | 'weeks'
  | 'w'
  | 'day'
  | 'days'
  | 'd'
  | 'hour'
  | 'hours'
  | 'h'
  | 'minute'
  | 'minutes'
  | 'm'
  | 'second'
  | 'seconds'
  | 's'
  | 'millisecond'
  | 'milliseconds'
  | 'ms'
  | 'quarter'
  | 'quarters'
  | 'Q';

export class TimeUtility {
  /**
   * @deprecated formatTime
   */
  static getDateFormatted = _4.curry((format: string, _time: time) => {
    return TimeUtility.__parseTime(_time).format(format);
  });

  /**
   * @deprecated formatMonthDayYear
   */
  static getDateMonthDayYear = TimeUtility.getDateFormatted('MMM D, YYYY');

  /**
   * @deprecated formatWeekdayMonthDay
   */
  static getWeekDayMonthDay = TimeUtility.getDateFormatted('ddd, MMM D');

  /**
   * @deprecated formatMonthDay
   */
  static getDateMonthDay = TimeUtility.getDateFormatted('MMM D');

  /**
   * @deprecated __parseTime internally or parseTime externally
   */
  static getMoment(_time: time) {
    if (typeof _time === 'number') {
      return moment.unix(_time);
    } else if (moment.isMoment(_time)) {
      return _time;
    } else {
      return moment(_time);
    }
  }

  static formatTime = _4.curry((format: string, _time: time) => {
    return TimeUtility.__parseTime(_time).format(format);
  });

  static parseTime = _4.curry((format: string, _time: time) => {
    return TimeUtility.__parseTime(_time, format);
  });

  private static __parseTime = (_time: time, format: string = ''): moment.Moment => {
    if (typeof _time === 'number') {
      return moment.unix(_time);
    } else if (moment.isMoment(_time)) {
      return _time;
    } else {
      return moment(_time as string, format);
    }
  };

  // formatters

  static formatMonthDayYear = TimeUtility.getDateFormatted('MMM D, YYYY');
  static formatWeekdayMonthDay = TimeUtility.getDateFormatted('ddd, MMM D');
  static formatMonthDay = TimeUtility.getDateFormatted('MMM D');

  // parsers

  static parseYearMonthDay = TimeUtility.parseTime(`YYYY-MM-DD`);

  // calculations

  static isAfterDate = _4.curry((time1: time, time2: time) => {
    const _time1 = TimeUtility.__parseTime(time1);
    const _time2 = TimeUtility.__parseTime(time2);
    return _time2.isAfter(_time1);
  });

  static isBeforeDate = _4.curry((time1: time, time2: time) => {
    const _time1 = TimeUtility.__parseTime(time1);
    const _time2 = TimeUtility.__parseTime(time2);
    return _time2.isBefore(_time1);
  });

  static isBeforeNow = _4.curry((_time: time) => TimeUtility.isBeforeDate(moment(), _time));
  static isAfterNow = _4.curry((_time: time) => TimeUtility.isAfterDate(moment(), _time));
  static isBeforeEndOfToday = _4.curry((_time: time) =>
    TimeUtility.isBeforeDate(moment(), TimeUtility.__parseTime(_time).endOf('day'))
  );
  static isAfterEndOfToday = _4.curry((_time: time) =>
    TimeUtility.isAfterDate(moment(), TimeUtility.__parseTime(_time).endOf('day'))
  );
  static getDurationByUnit = _4.curry((unit: timeunits, diff: number) => moment.duration(diff, unit as timeunits));
  static getDurationMilliseconds = TimeUtility.getDurationByUnit('milliseconds');

  static getDiffBetweenTimes = _4.curry((unit: timeunits, time1: time, time2: time) => {
    const _time1 = TimeUtility.__parseTime(time1);
    const _time2 = TimeUtility.__parseTime(time2);
    return _time1.diff(_time2, unit as timeunits);
  });

  static getDiffBetweenTimesMilliseconds = TimeUtility.getDiffBetweenTimes('milliseconds');
  static getDiffBetweenTimesDays = TimeUtility.getDiffBetweenTimes('days');
  static getDaysBetweenNowAndTime = _4.curry((_time: time) => TimeUtility.getDiffBetweenTimesDays(moment(), _time));

  static getHumanTimeDiff = _4.curry((time1: time, time2: time) =>
    TimeUtility.getDurationMilliseconds(TimeUtility.getDiffBetweenTimesMilliseconds(time1, time2)).humanize()
  );

  static isSameByUnit = _4.curry((unit: timeunits, time1: time, time2: time) => {
    const _time1 = TimeUtility.__parseTime(time1);
    const _time2 = TimeUtility.__parseTime(time2);
    return _time1.isSame(_time2, unit);
  });
  static isSameMillisecond = TimeUtility.isSameByUnit('millisecond');
  static isSameYear = TimeUtility.isSameByUnit('year');

  static isCurrentYear = _4.curry((_time: time) => TimeUtility.isSameYear(moment(), _time));
}



// WEBPACK FOOTER //
// ./app/vendor/utility-ts-type/src/types/time.ts